import React, { useEffect } from 'react'
import { FormField, FormReadonlyField, FormSelectField } from '@/components/Form/FormField'
import { PaymentProviderFields } from 'api/PaymentProvider/Types/PaymentProviderFields'
import { CurrencyType, DragonPhoenixCurrencyType, PHPCurrencyType } from 'api/Wallet/Types/Wallet'
import { FormikErrors, useFormikContext } from 'formik'
import { capitalize } from 'lodash'
import { DepositFormValues } from 'routes/Transactions/Create/Deposit/DepositForm'
import { WithdrawalFormValues } from 'routes/Transactions/Create/Withdrawal/WithdrawalForm'
import { TransactionPaymentProviderDifferentCurrencyField } from 'utils/transaction/status.index'

interface Prop {
  paymentProviderFields: PaymentProviderFields[]
  errors: FormikErrors<WithdrawalFormValues | DepositFormValues>
  setFieldValue: (
    field: string,
    value: string | number | undefined,
    shouldValidate?: boolean
  ) => void
  setCurrency?: (
    currency: CurrencyType | DragonPhoenixCurrencyType | PHPCurrencyType | string
  ) => void
}

type TransactionFormValues = WithdrawalFormValues | DepositFormValues

const PaymentServiceProviderFields: React.FC<Prop> = ({
  errors,
  setFieldValue,
  paymentProviderFields,
  setCurrency,
}) => {
  const { values } = useFormikContext<TransactionFormValues>()
  const optionValues = (values.options ?? {}) as Record<string, string | number | undefined>
  const optionErrors = (errors.options ?? {}) as Record<string, string>

  useEffect(() => {
    const currencyField = paymentProviderFields.find(
      (field) => field.type === 'hidden' && field.field === 'payment_currency_id'
    )

    if (currencyField?.value && setCurrency) {
      setCurrency(currencyField.value as PHPCurrencyType | string)
    }
  }, [paymentProviderFields, setCurrency])

  return (
    <>
      {paymentProviderFields.map((paymentField) => {
        if (paymentField.clientFilled && paymentField.type === 'text') {
          return (
            <FormField
              key={paymentField.field}
              name={`options.${paymentField.field}`}
              label={capitalize(paymentField.name)}
              dataTest={`payment-${paymentField.field}-field`}
              error={optionErrors[paymentField.field]}
            />
          )
        }

        if (paymentField.type === 'select' && Array.isArray(paymentField.value)) {
          return (
            <FormSelectField
              key={paymentField.field}
              name={`options.${paymentField.field}`}
              label={paymentField.name}
              value={optionValues[paymentField.field]}
              options={paymentField.value.map((valueField) => ({
                id: valueField.value,
                name: valueField.name,
              }))}
              allowClear
              showSearch
              error={optionErrors[paymentField.field]}
              onChange={(value) => {
                const nextValue = value ? String(value) : undefined
                setFieldValue(`options.${paymentField.field}`, nextValue)
                if (
                  paymentField.field ===
                    TransactionPaymentProviderDifferentCurrencyField.PAYMENT_PROVIDER_CURRENCY_ID &&
                  setCurrency &&
                  nextValue
                ) {
                  setCurrency(nextValue)
                }
              }}
            />
          )
        }

        if (
          paymentField.field ===
          TransactionPaymentProviderDifferentCurrencyField.PAYMENT_PROVIDER_CURRENCY_ID
        ) {
          return (
            <FormReadonlyField
              key={paymentField.field}
              name={`options.${paymentField.field}`}
              label={capitalize(paymentField.name)}
              value={String(paymentField.value ?? '')}
            />
          )
        }

        return null
      })}
    </>
  )
}

export default PaymentServiceProviderFields
